import { Controller } from "@hotwired/stimulus"

const VISIBILITY_VALUES = new Set([ "private", "public" ])

export default class extends Controller {
  static targets = [
    "trigger",
    "label",
    "error",
    "busy",
    "makePublicIcon",
    "makePrivateIcon",
    "changeVisibilityIcon",
    "chooser",
    "chooserDialog",
    "chooserPublicRadio",
    "chooserPrivateRadio",
    "chooserError",
    "chooserSaveButton",
    "chooserCancelButton"
  ]
  static values = {
    current: String,
    profilePublic: Boolean,
    profileGate: Boolean,
    resourceNoun: String,
    updateUrl: String,
    parameterRoot: String,
    parameterName: String,
    privateRequestValue: String,
    publicRequestValue: String,
    makePublicLabel: String,
    makePrivateLabel: String,
    changeVisibilityLabel: String
  }

  connect() {
    this.pendingVisibility = null
    this.requestInFlight = false
    this.previousChooserFocus = null
    this.boundResetForTurboCache = this.resetForTurboCache.bind(this)
    document.addEventListener("turbo:before-cache", this.boundResetForTurboCache)
    this.syncState()
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.boundResetForTurboCache)
    this.resetForTurboCache()
  }

  toggleVisibility(event) {
    event.preventDefault()
    if (this.requestInFlight) return

    this.clearError()
    if (this.currentValue === "public" && !this.profilePublicValue && this.hasChooserTarget) {
      this.openChooser(event)
      return
    }

    const nextValue = this.currentValue === "public" ? "private" : "public"

    if (nextValue === "public" && this.profileGateValue && !this.profilePublicValue) {
      this.pendingVisibility = nextValue
      this.dispatch("request-public")
      return
    }

    this.persist(nextValue)
  }

  openChooser(event) {
    if (!this.hasChooserTarget) return

    event.preventDefault()
    this.previousChooserFocus = event.currentTarget
    this.syncChooserSelection()
    this.clearError()
    this.chooserTarget.classList.remove("hidden")
    this.chooserTarget.setAttribute("aria-hidden", "false")
    this.triggerTarget.setAttribute("aria-expanded", "true")

    requestAnimationFrame(() => {
      if (this.chooserIsOpen() && this.hasChooserPublicRadioTarget) {
        this.chooserPublicRadioTarget.focus()
      }
    })
  }

  saveChooserSelection(event) {
    event.preventDefault()
    if (!this.hasChooserTarget || this.requestInFlight) return

    const selectedValue = this.hasChooserPublicRadioTarget && this.chooserPublicRadioTarget.checked
      ? "public"
      : "private"

    if (!VISIBILITY_VALUES.has(selectedValue) || selectedValue === this.currentValue) {
      this.closeChooser()
      return
    }

    this.persist(selectedValue)
  }

  cancelChooser(event) {
    if (event) event.preventDefault()
    this.closeChooser()
  }

  closeChooserOnBackdrop(event) {
    if (event.target === this.chooserTarget) this.closeChooser()
  }

  trapChooserFocus(event) {
    if (!this.chooserIsOpen() || event.key !== "Tab" || !this.hasChooserDialogTarget) return

    const focusable = Array.from(this.chooserDialogTarget.querySelectorAll(
      "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
    ))
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (!this.chooserDialogTarget.contains(document.activeElement)) {
      event.preventDefault()
      const nextFocusable = event.shiftKey ? last : first
      nextFocusable.focus()
      return
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
      return
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  closeChooser({ remove = false } = {}) {
    if (this.hasChooserTarget) {
      this.chooserTarget.classList.add("hidden")
      this.chooserTarget.setAttribute("aria-hidden", "true")
      if (remove) this.chooserTarget.remove()
    }

    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "false")
      if (remove) this.triggerTarget.removeAttribute("aria-haspopup")
    }

    this.syncChooserSelection()
    const previousFocus = this.previousChooserFocus
    this.previousChooserFocus = null
    if (previousFocus && document.contains(previousFocus)) previousFocus.focus()
  }

  chooserIsOpen() {
    return this.hasChooserTarget && !this.chooserTarget.classList.contains("hidden")
  }

  syncChooserSelection() {
    if (this.hasChooserPublicRadioTarget) this.chooserPublicRadioTarget.checked = this.currentValue === "public"
    if (this.hasChooserPrivateRadioTarget) this.chooserPrivateRadioTarget.checked = this.currentValue === "private"
  }

  resetForTurboCache() {
    if (this.chooserIsOpen()) this.closeChooser()
    this.clearError()
    this.setBusy(false)
  }

  profilePublicConfirmed() {
    this.profilePublicValue = true
    const pendingVisibility = this.pendingVisibility
    this.pendingVisibility = null
    if (pendingVisibility) this.persist(pendingVisibility)
  }

  profilePublicCancelled() {
    this.pendingVisibility = null
  }

  async persist(value) {
    if (this.requestInFlight || !VISIBILITY_VALUES.has(value)) return

    this.pendingVisibility = null
    this.clearError()
    this.requestInFlight = true
    this.setBusy(true)

    try {
      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: this.requestBody(value)
      })
      const payload = await this.parseJson(response)

      if (!response.ok) {
        throw new Error(payload.errors?.[0] || this.defaultErrorMessage())
      }

      const persistedValue = payload.value?.toString()
      if (!VISIBILITY_VALUES.has(persistedValue)) {
        throw new Error(this.defaultErrorMessage())
      }

      this.currentValue = persistedValue
      this.syncState()
      if (persistedValue === "private" && this.hasChooserTarget) this.closeChooser({ remove: true })
      this.dispatch("changed", { detail: { value: persistedValue, label: payload.label } })
    } catch (error) {
      this.showError(error.message || this.defaultErrorMessage())
    } finally {
      this.requestInFlight = false
      this.setBusy(false)
    }
  }

  syncState() {
    if (!VISIBILITY_VALUES.has(this.currentValue)) return

    const nextActionLabel = this.currentValue === "public"
      ? (!this.profilePublicValue && this.hasChooserTarget ? this.changeVisibilityLabelValue : this.makePrivateLabelValue)
      : this.makePublicLabelValue

    if (this.hasTriggerTarget) {
      if (this.hasLabelTarget) {
        this.labelTarget.textContent = nextActionLabel
      } else {
        this.triggerTarget.textContent = nextActionLabel
      }
      this.triggerTarget.setAttribute("aria-label", nextActionLabel)
      if (this.hasChooserTarget && this.currentValue === "public" && !this.profilePublicValue) {
        this.triggerTarget.setAttribute("aria-haspopup", "dialog")
      } else {
        this.triggerTarget.removeAttribute("aria-haspopup")
        this.triggerTarget.removeAttribute("aria-expanded")
      }
    }

    const changeVisibilityIconActive = this.currentValue === "public" && !this.profilePublicValue && this.hasChooserTarget

    if (this.hasMakePublicIconTarget) {
      this.setTargetHidden(this.makePublicIconTarget, this.currentValue !== "private")
    }
    if (this.hasMakePrivateIconTarget) {
      this.setTargetHidden(this.makePrivateIconTarget, this.currentValue !== "public" || changeVisibilityIconActive)
    }
    if (this.hasChangeVisibilityIconTarget) {
      this.setTargetHidden(this.changeVisibilityIconTarget, !changeVisibilityIconActive)
    }

    this.element.dataset.inlineVisibilityState = this.currentValue
  }

  setTargetHidden(target, hidden) {
    target.hidden = hidden
    target.classList.toggle("hidden", hidden)
  }

  setBusy(busy) {
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-disabled", busy.toString())
      this.triggerTarget.setAttribute("aria-busy", busy.toString())
    }
    if (this.hasBusyTarget) {
      this.busyTarget.hidden = !busy
      this.busyTarget.classList.toggle("hidden", !busy)
    }
    if (this.hasChooserSaveButtonTarget) this.chooserSaveButtonTarget.disabled = busy
    if (this.hasChooserCancelButtonTarget) this.chooserCancelButtonTargets.forEach((button) => { button.disabled = busy })
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ""
      this.errorTarget.hidden = true
      this.errorTarget.classList.add("hidden")
    }
    if (this.hasChooserErrorTarget) {
      this.chooserErrorTarget.textContent = ""
      this.chooserErrorTarget.hidden = true
      this.chooserErrorTarget.classList.add("hidden")
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.hidden = false
      this.errorTarget.classList.remove("hidden")
    }
    if (this.chooserIsOpen() && this.hasChooserErrorTarget) {
      this.chooserErrorTarget.textContent = message
      this.chooserErrorTarget.hidden = false
      this.chooserErrorTarget.classList.remove("hidden")
    }
  }

  requestBody(value) {
    const formData = new FormData()
    const requestValue = value === "public" ? this.publicRequestValueValue : this.privateRequestValueValue
    formData.append(`${this.parameterRootValue}[${this.parameterNameValue}]`, requestValue)
    return formData
  }

  defaultErrorMessage() {
    return `Evercooked could not update your ${this.resourceNounValue} visibility. The saved state is unchanged.`
  }

  async parseJson(response) {
    try {
      return await response.json()
    } catch {
      return {}
    }
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
